import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Box, Container, Grid, Typography, TextField, Backdrop, CircularProgress,
} from '../../../components/mui/Core/index'
import InputAdornment from '@mui/material/InputAdornment'
import SearchIcon from '@mui/icons-material/Search'
import HoldAllocationNav from '../components/HoldAllocationNav/hold-allocation-nav'
import DashboardBuckets from './components/DashboardBuckets/dashboard-buckets'
import HoldOrderTable from './components/HoldOrderTable/hold-order-table'
import PageTitle from '../../../components/PageTitle/page-title'
import GenericSnackbar from '../../../components/GenericSnackbar/generic-snackbar'
import {
  fetchDashboard,
  fetchOrders,
  setSelectedBucket,
  setSearchTerm,
} from '../hold-allocation-slice'
import { setSnackbar } from '../../../components/GenericSnackbar/generic-snackbar-slice'
import { HOLD_ALLOCATION_BUCKETS } from '../../../constants/hold-allocation-constants'

function HoldAllocationOrders() {
  const dispatch = useDispatch()
  const dashboard = useSelector((s) => s.holdAllocation.dashboard)
  const orders = useSelector((s) => s.holdAllocation.orders)
  const isFetching = useSelector((s) => s.holdAllocation.isFetching)
  const selectedBucket = useSelector((s) => s.holdAllocation.selectedBucket)
  const error = useSelector((s) => s.holdAllocation.error)

  const [mockRole, setMockRole] = useState('CUSTOMER_SERVICE')
  const [searchInput, setSearchInput] = useState('')

  const loadData = useCallback(
    (bucket, search, role) => {
      const params = { role }
      if (bucket && bucket !== HOLD_ALLOCATION_BUCKETS.ALL) params.bucket = bucket
      if (search) params.search = search
      dispatch(fetchDashboard(role))
      dispatch(fetchOrders(params))
    },
    [dispatch],
  )

  useEffect(() => {
    loadData(selectedBucket, '', mockRole)
  }, [loadData, mockRole, selectedBucket])

  useEffect(() => {
    if (error) {
      dispatch(setSnackbar({ message: error, severity: 'error', action: true, autoHideDuration: 4000 }))
    }
  }, [error, dispatch])

  const handleBucketClick = (bucket) => {
    dispatch(setSelectedBucket(bucket))
  }

  const handleRoleChange = (role) => {
    setMockRole(role)
  }

  const handleSearchChange = (e) => {
    const val = e.target.value
    setSearchInput(val)
    dispatch(setSearchTerm(val))
    const params = { role: mockRole, search: val }
    if (selectedBucket !== HOLD_ALLOCATION_BUCKETS.ALL) params.bucket = selectedBucket
    dispatch(fetchOrders(params))
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#f2f5f2', minHeight: '100vh' }}>
      <HoldAllocationNav mockRole={mockRole} onRoleChange={handleRoleChange} />
      <Backdrop open={isFetching} sx={{ zIndex: 9999 }}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Container maxWidth='xl' sx={{ pt: 3, pb: 6 }}>
        <Grid container spacing={3}>
          {/* Page header */}
          <Grid size={{ xs: 12 }} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
            <PageTitle title='Hold &amp; Allocation' />
            <TextField
              size='small'
              placeholder='Search by order, PO, ship-to…'
              value={searchInput}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchIcon sx={{ fontSize: 20, color: '#606060' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 320,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  fontFamily: 'AtlasGroteskRegular',
                  fontSize: '0.875rem',
                },
              }}
            />
          </Grid>

          {/* Dashboard buckets */}
          <Grid size={{ xs: 12 }}>
            <DashboardBuckets
              dashboard={dashboard}
              selectedBucket={selectedBucket}
              onBucketClick={handleBucketClick}
            />
          </Grid>

          {/* Orders table */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'SanomatMedium',
                  fontSize: '1.2rem',
                  color: '#145a4b',
                }}
              >
                Orders
              </Typography>
              <Typography
                sx={{
                  ml: 1.5,
                  fontFamily: 'AtlasGroteskRegular',
                  fontSize: '0.85rem',
                  color: '#606060',
                }}
              >
                {orders.length} {orders.length === 1 ? 'result' : 'results'}
              </Typography>
            </Box>
            <HoldOrderTable orders={orders} isFetching={isFetching} />
          </Grid>
        </Grid>
      </Container>
      <GenericSnackbar />
    </Box>
  )
}

export default HoldAllocationOrders
